import { useState, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Conferencia as EstadoDaConferencia, LinhaDaConferencia, PartType } from '@core/types';
import Layout from '../componentes/Layout';
import Aviso from '../componentes/Aviso';
import Carregando from '../componentes/Carregando';
import Segmentado from '../componentes/Segmentado';
import FormularioDoBalcao from '../componentes/FormularioDoBalcao';
import { filaDeConferencia, habilitarPeca, recusarPeca, tiposDePeca } from '../lib/consultas';
import {
  contagemDeclarada, dadosDaLinha, dadosDoRascunho, linhasDaRasura, problemaDoRascunho,
  rascunhoDaLinha, saldoAtual, saldoContado, valorLegivel, type RascunhoDoBalcao,
} from '../lib/cadastroDoBalcao';
import { brl, dataBR, dataHoraBR, qtd } from '../lib/formato';
import { podeConferirCadastro, useSessao } from '../lib/sessao';
import { mensagemDeErro } from '../lib/supabase';

// =============================================================================
// A FILA DE CONFERÊNCIA (093, PR 2 — 08/10/2026). Uma rota, duas telas, no
// molde de Pedidos.tsx:
//
//   * a GERÊNCIA (gerente e dono, decisão d) vê as peças que o vendedor
//     cadastrou e LIBERA com um toque — sem ir à prateleira (decisão b) —,
//     CORRIGE e libera, ou RECUSA com motivo;
//   * o VENDEDOR vê as peças DELE: a que espera, a liberada com a RASURA do que
//     a gerência mudou (permanente, o pedido do dono: "no modelo
//     rasura-correção") e a recusada, riscada, com o motivo.
//
// O verbo da tela é "liberar", a palavra do dono. "Conferência" já tem dois
// sentidos na casa (o Estoque e a venda conferida do caixa), e por isso só o
// item de menu da gerência a carrega — para casar com a frase com que o banco
// recusa a venda da peça pendente.
//
// Nada de venda, total ou custo aqui (faturamento só do dono, 28/09/2026): o
// retorno da RPC tem colunas fixas, e nenhuma é sensível.
// =============================================================================

const ABAS: ReadonlyArray<{ id: EstadoDaConferencia; rotulo: string }> = [
  { id: 'pendente', rotulo: 'Aguardando' },
  { id: 'habilitado', rotulo: 'Liberadas' },
  { id: 'recusado', rotulo: 'Recusadas' },
];

export default function Conferencia() {
  const { papel } = useSessao();
  if (!podeConferirCadastro(papel)) return <MinhasPecas />;
  return <PecasParaLiberar />;
}

function useFila(aba: EstadoDaConferencia) {
  return useQuery({ queryKey: ['fila-conferencia', aba], queryFn: () => filaDeConferencia(aba) });
}

function useTipos() {
  return useQuery({ queryKey: ['tipos-de-peca'], queryFn: tiposDePeca, staleTime: 10 * 60_000 }).data ?? [];
}

// --- O lado de quem CADASTROU -----------------------------------------------

function MinhasPecas() {
  const [aba, setAba] = useState<EstadoDaConferencia>('pendente');
  const fila = useFila(aba);
  const tipos = useTipos();

  return (
    <Layout titulo="Minhas peças cadastradas">
      <div className="space-y-4">
        <p className="text-sm text-mute">
          As peças que você cadastrou no balcão. Cada uma vende depois que o gerente ou o dono
          libera; o que ele mudou fica marcado, e a recusada fica riscada, com o motivo.
        </p>
        <Segmentado itens={ABAS} valor={aba} aoMudar={setAba} />
        <ListaDaFila fila={fila} vazio={VAZIO_MINHAS[aba]}>
          {(l) => <CartaoDaMinhaPeca linha={l} tipos={tipos} />}
        </ListaDaFila>
      </div>
    </Layout>
  );
}

const VAZIO_MINHAS: Record<EstadoDaConferencia, string> = {
  pendente: 'Nenhuma peça sua esperando liberação.',
  habilitado: 'Nenhuma peça sua liberada ainda.',
  recusado: 'Nenhuma peça sua foi recusada.',
};

/** O cartão de UMA peça na lista de quem a cadastrou. */
export function CartaoDaMinhaPeca({ linha, tipos = [] }: {
  linha: LinhaDaConferencia;
  tipos?: ReadonlyArray<PartType>;
}) {
  const contou = contagemDeclarada(linha);

  if (linha.conferencia === 'recusado' || linha.product_id === null) {
    return (
      <div className="cartao space-y-1 p-3 opacity-80">
        <LinhaRecusada linha={linha} />
      </div>
    );
  }

  return (
    <div className="cartao space-y-2 p-3">
      <NomeDaPeca linha={linha} />
      {linha.conferencia === 'pendente' ? (
        <p className="flex flex-wrap items-center gap-2 text-xs">
          <span className="border border-alerta/60 px-1.5 py-0.5 font-mono text-alerta">aguardando liberação</span>
          {contou !== null && <span className="text-mute">você contou {qtd(contou)}</span>}
          <span className="text-mute">{brl(linha.preco_balcao)}</span>
        </p>
      ) : (
        <>
          <p className="text-xs text-ok">
            Liberada{linha.conferido_em ? ` em ${dataBR(linha.conferido_em)}` : ''}
            {linha.conferido_por_nome ? ` por ${linha.conferido_por_nome}` : ''}
          </p>
          <Rasura correcoes={linha.correcoes} tipos={tipos} />
        </>
      )}
    </div>
  );
}

// --- O lado de quem LIBERA ----------------------------------------------------

function PecasParaLiberar() {
  const [aba, setAba] = useState<EstadoDaConferencia>('pendente');
  const fila = useFila(aba);
  const tipos = useTipos();

  return (
    <Layout titulo="Peças para liberar">
      <div className="space-y-4">
        <p className="text-sm text-mute">
          O que o balcão cadastrou e ainda não vende. Liberar não exige ir à prateleira: a
          quantidade que a pessoa contou vira o estoque, a não ser que a peça já tenha saldo
          contado pela gerência. Dar entrada de mercadoria numa peça daqui também a libera.
        </p>
        <Segmentado itens={ABAS} valor={aba} aoMudar={setAba} />
        <ListaDaFila fila={fila} vazio={VAZIO_GERENCIA[aba]}>
          {(l) => <CartaoParaLiberar linha={l} tipos={tipos} />}
        </ListaDaFila>
      </div>
    </Layout>
  );
}

const VAZIO_GERENCIA: Record<EstadoDaConferencia, string> = {
  pendente: 'Nenhuma peça esperando liberação.',
  habilitado: 'Nenhuma peça liberada pela fila ainda.',
  recusado: 'Nenhuma peça recusada.',
};

/** O cartão de UMA peça na fila da gerência — com os três gestos na pendente,
 *  e o caminho de volta na recusada. */
export function CartaoParaLiberar({ linha, tipos = [] }: {
  linha: LinhaDaConferencia;
  tipos?: ReadonlyArray<PartType>;
}) {
  const cliente = useQueryClient();
  const [modo, setModo] = useState<'ver' | 'corrigir' | 'recusar'>('ver');
  // RASCUNHO ?? SERVIDOR, nunca um useEffect que copia o servidor para o
  // formulário: com ele, a consulta da fila respondendo tarde apagaria o que o
  // gerente acabou de digitar (a armadilha da folha, no CLAUDE.md).
  const [rascunho, setRascunho] = useState<RascunhoDoBalcao | null>(null);
  const [motivo, setMotivo] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const id = linha.product_id;
  const recarregar = () => {
    cliente.invalidateQueries({ queryKey: ['fila-conferencia'] });
    cliente.invalidateQueries({ queryKey: ['buscar'] });
    cliente.invalidateQueries({ queryKey: ['sino', 'para-liberar'] });
    if (id) cliente.invalidateQueries({ queryKey: ['peca', id] });
  };

  const liberar = useMutation({
    mutationFn: (dados: Parameters<typeof habilitarPeca>[1]) => habilitarPeca(id!, dados),
    onSuccess: recarregar,
  });
  const recusar = useMutation({
    mutationFn: (m: string) => recusarPeca(id!, m),
    onSuccess: recarregar,
  });
  const erro = liberar.error ?? recusar.error;
  const ocupado = liberar.isPending || recusar.isPending;

  // O "Liberar" de um toque manda o cartão como está. Se o cartão não passa na
  // régua (peça sem preço), o botão não finge: manda para o Corrigir.
  const problemaDoCartao = problemaDoRascunho(rascunhoDaLinha(linha));
  const valores = rascunho ?? rascunhoDaLinha(linha);

  if (linha.conferencia === 'habilitado') {
    return (
      <div className="cartao space-y-2 p-3">
        <Cabecalho linha={linha} />
        <NomeDaPeca linha={linha} />
        <p className="text-xs text-ok">
          Liberada{linha.conferido_em ? ` em ${dataBR(linha.conferido_em)}` : ''}
          {linha.conferido_por_nome ? ` por ${linha.conferido_por_nome}` : ''}
        </p>
        <Rasura correcoes={linha.correcoes} tipos={tipos} semCorrecao="Liberada sem correção." />
      </div>
    );
  }

  const recusada = linha.conferencia === 'recusado' || id === null;

  return (
    <div className={`cartao space-y-3 p-3 ${recusada && modo === 'ver' ? 'opacity-90' : ''}`}>
      <Cabecalho linha={linha} />
      {recusada ? <LinhaRecusada linha={linha} /> : <NomeDaPeca linha={linha} />}
      {!recusada && <FichaDoCartao linha={linha} tipos={tipos} />}
      {/* "Liberar mesmo assim" segue a mesma régua: a recusada que a gerência
          contou enquanto pendente também mantém o saldo. */}
      {recusada && id && <AvisoDoSaldoContado linha={linha} />}

      {modo === 'corrigir' && id && (
        <div className="space-y-3 border-t border-edge pt-3">
          <FormularioDoBalcao valores={valores} aoMudar={setRascunho} tipos={tipos} />
          <DicaDaQuantidadeNoCorrigir linha={linha} />
          {aviso && <Aviso tipo="erro">{aviso}</Aviso>}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" className="botao-primario" disabled={ocupado}
                    onClick={() => {
                      const problema = problemaDoRascunho(valores);
                      if (problema) { setAviso(problema); return; }
                      setAviso(null);
                      liberar.mutate(dadosDoRascunho(valores, null));
                    }}>
              {liberar.isPending ? 'Liberando…' : 'Liberar com a correção'}
            </button>
            <button type="button" className="botao-secundario" disabled={ocupado}
                    onClick={() => { setModo('ver'); setRascunho(null); setAviso(null); }}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {modo === 'recusar' && id && (
        <FormularioDeRecusa id={id} motivo={motivo} aoMudar={setMotivo} ocupado={ocupado}
                            recusando={recusar.isPending}
                            aoConfirmar={() => recusar.mutate(motivo.trim())}
                            aoCancelar={() => { setModo('ver'); setMotivo(''); }} />
      )}

      {erro ? <Aviso tipo="erro">{mensagemDeErro(erro)}</Aviso> : null}

      {modo === 'ver' && id && (
        <div className="flex flex-wrap gap-2">
          <button type="button" className="botao-primario min-h-11 flex-1 sm:flex-none" disabled={ocupado || problemaDoCartao !== null}
                  onClick={() => liberar.mutate(dadosDaLinha(linha))}>
            {liberar.isPending ? 'Liberando…' : recusada ? 'Liberar mesmo assim' : 'Liberar'}
          </button>
          <button type="button" className="botao-secundario min-h-11 flex-1 sm:flex-none" disabled={ocupado}
                  onClick={() => { setModo('corrigir'); setRascunho(null); setAviso(null); }}>
            Corrigir
          </button>
          {!recusada && (
            <button type="button" className="botao-perigo min-h-11 flex-1 sm:flex-none" disabled={ocupado}
                    onClick={() => setModo('recusar')}>
              Recusar
            </button>
          )}
        </div>
      )}
      {modo === 'ver' && id && problemaDoCartao && (
        <p className="text-xs text-alerta">{problemaDoCartao} Use Corrigir.</p>
      )}
    </div>
  );
}

/** A recusa pede o MOTIVO — é o que quem cadastrou vai ler, riscado, na lista
 *  dele. O botão nasce travado e só solta com texto: recusa muda é o que faz o
 *  colaborador cadastrar igual amanhã. */
export function FormularioDeRecusa({
  id, motivo, aoMudar, aoConfirmar, aoCancelar, ocupado = false, recusando = false,
}: {
  id: string;
  motivo: string;
  aoMudar: (m: string) => void;
  aoConfirmar: () => void;
  aoCancelar: () => void;
  ocupado?: boolean;
  recusando?: boolean;
}) {
  return (
    <div className="space-y-2 border-t border-edge pt-3">
      <label className="rotulo" htmlFor={`motivo-${id}`}>Motivo da recusa (quem cadastrou vai ler)</label>
      <input id={`motivo-${id}`} className="campo" value={motivo} autoComplete="off"
             placeholder="Ex.: já existe como PASTIL-0012; preço errado"
             onChange={(e) => aoMudar(e.target.value)} />
      <div className="flex flex-col gap-2 sm:flex-row">
        <button type="button" className="botao-perigo" disabled={ocupado || !motivo.trim()}
                onClick={aoConfirmar}>
          {recusando ? 'Recusando…' : 'Confirmar recusa'}
        </button>
        <button type="button" className="botao-secundario" disabled={ocupado} onClick={aoCancelar}>
          Cancelar
        </button>
      </div>
    </div>
  );
}

// --- Peças comuns ---------------------------------------------------------------

function ListaDaFila({ fila, vazio, children }: {
  fila: ReturnType<typeof useFila>;
  vazio: string;
  children: (l: LinhaDaConferencia) => ReactNode;
}) {
  if (fila.isLoading) return <Carregando />;
  if (fila.error) return <Aviso tipo="erro">{mensagemDeErro(fila.error)}</Aviso>;
  const linhas = fila.data ?? [];
  if (linhas.length === 0) return <p className="text-sm text-dim">{vazio}</p>;
  return (
    <ul className="space-y-2">
      {linhas.map((l) => (
        <li key={l.product_id ?? `${l.sku}-${l.criado_em}`}>{children(l)}</li>
      ))}
    </ul>
  );
}

/** Quem cadastrou e quando — o que a gerência precisa para saber a quem
 *  perguntar. */
function Cabecalho({ linha }: { linha: LinhaDaConferencia }) {
  return (
    <p className="text-xs text-dim">
      {linha.autor_nome && linha.autor_nome !== '—' ? linha.autor_nome : 'Sem autor conhecido'}
      {' '}cadastrou em {dataHoraBR(linha.criado_em)}
    </p>
  );
}

/** O nome, com link para a ficha quando a peça ainda existe. */
function NomeDaPeca({ linha }: { linha: LinhaDaConferencia }) {
  return (
    <div className="min-w-0">
      {linha.product_id ? (
        <Link to={`/peca/${linha.product_id}`} className="font-bold leading-snug hover:text-signal">
          {linha.descricao}
        </Link>
      ) : (
        <p className="font-bold leading-snug">{linha.descricao}</p>
      )}
      <p className="font-mono text-[0.6875rem] uppercase tracking-wider text-dim">{linha.sku}</p>
    </div>
  );
}

/** Os campos do cadastro, como o colaborador os deixou — e o saldo de HOJE,
 *  quando a gerência já contou a prateleira antes de liberar. */
export function FichaDoCartao({ linha, tipos = [] }: {
  linha: LinhaDaConferencia;
  tipos?: ReadonlyArray<PartType>;
}) {
  const contou = contagemDeclarada(linha);
  const itens: Array<[string, string, boolean?]> = [
    ['Código', linha.codigo_fabrica ?? '—', true],
    ['Marca', linha.marca ?? '—'],
    ['Tipo', linha.tipo ? valorLegivel('tipo', linha.tipo, tipos) : 'Sem tipo'],
    ['Preço de balcão', brl(linha.preco_balcao)],
    ['Onde fica', linha.local ?? '—', true],
    ['Contou', contou === null ? '—' : `${qtd(contou)} un`],
  ];
  if (saldoContado(linha)) itens.push(['Saldo agora', `${qtd(saldoAtual(linha))} un`]);
  return (
    <>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs sm:grid-cols-3">
        {itens.map(([rotulo, valor, mono]) => (
          <div key={rotulo} className="min-w-0">
            <dt className="text-dim">{rotulo}</dt>
            <dd className={`truncate text-paper ${mono ? 'font-mono' : ''}`}>{valor}</dd>
          </div>
        ))}
      </dl>
      <AvisoDoSaldoContado linha={linha} />
    </>
  );
}

/** Achado da revisão (09/10/2026): sem esta linha, quem contou 5 e tocou em
 *  Liberar não sabia se os 3 declarados iam por cima. O banco (habilitar_peca)
 *  mantém o saldo contado; a tela diz isso ANTES do toque, com a mesma régua
 *  (saldo_contado, que a fila devolve pronta) — inclusive o saldo 0 contado,
 *  que "saldo > 0" calava (2ª revisão). */
export function AvisoDoSaldoContado({ linha }: { linha: LinhaDaConferencia }) {
  if (!saldoContado(linha)) return null;
  return (
    <p className="text-xs text-mute">
      Esta peça já tem saldo contado: {qtd(saldoAtual(linha))} un. Liberar mantém esse saldo: a
      contagem do balcão não passa por cima.
    </p>
  );
}

/** No Corrigir, o que o número da quantidade faz quando a peça já tem saldo
 *  contado — a régua de habilitar_peca dita em português: a declaração (o
 *  número com que o formulário começa) mantém o saldo; outro número é contagem
 *  da gerência e vira o saldo novo. */
export function DicaDaQuantidadeNoCorrigir({ linha }: { linha: LinhaDaConferencia }) {
  if (!saldoContado(linha)) return null;
  const contou = contagemDeclarada(linha);
  return (
    <p className="text-xs text-dim">
      {contou === null ? 'Deixar a quantidade em branco' : `Deixar a quantidade em ${qtd(contou)}`}
      {' '}mantém o saldo de {qtd(saldoAtual(linha))} un. Outro número vira o saldo novo.
    </p>
  );
}

/** A linha RISCADA, com o par acessível: o leitor de tela não enxerga o risco,
 *  então ele ouve "Recusada:" antes do nome (o molde das contas canceladas). */
function LinhaRecusada({ linha }: { linha: LinhaDaConferencia }) {
  const apagada = linha.product_id === null;
  return (
    <>
      <p className="text-sm font-bold line-through decoration-erro/60">
        <span className="sr-only">Recusada: </span>{linha.descricao}
      </p>
      <p className="font-mono text-[0.6875rem] uppercase tracking-wider text-dim">{linha.sku}</p>
      <p className="text-xs text-erro">
        {linha.motivo || !apagada ? 'Recusada' : 'Saiu do catálogo'}
        {linha.conferido_em ? ` em ${dataBR(linha.conferido_em)}` : ''}
        {linha.conferido_por_nome ? ` por ${linha.conferido_por_nome}` : ''}
        {linha.motivo ? ` — ${linha.motivo}` : ''}
      </p>
      {apagada && <p className="text-xs text-dim">A peça foi apagada do catálogo.</p>}
    </>
  );
}

/** A RASURA (pedido do dono): o que a gerência mudou ao liberar, riscado e
 *  corrigido, para sempre na lista de quem cadastrou. */
export function Rasura({ correcoes, tipos = [], semCorrecao = 'Liberada como você cadastrou.' }: {
  correcoes: LinhaDaConferencia['correcoes'];
  tipos?: ReadonlyArray<PartType>;
  /** O que se diz quando a gerência não mudou nada. */
  semCorrecao?: string;
}) {
  const linhas = linhasDaRasura(correcoes, tipos);
  if (linhas.length === 0) return <p className="text-xs text-mute">{semCorrecao}</p>;
  return (
    <ul className="space-y-1 text-xs">
      {linhas.map((c) => (
        <li key={c.campo} className="flex flex-wrap items-baseline gap-x-1.5">
          <span className="text-dim">{c.rotulo}:</span>
          <span className="text-dim line-through decoration-erro/60">
            <span className="sr-only">Era: </span>{c.de}
          </span>
          <span aria-hidden="true">→</span>
          <strong className="text-paper"><span className="sr-only">Ficou: </span>{c.para}</strong>
        </li>
      ))}
    </ul>
  );
}
